/**
 * What a line typed in the chat box asks for (unit 26B; v1's `ChatStrip`
 * dispatch). Only an agent turn needs the agent; everything else is done here,
 * at once, whether or not a run is in flight.
 *
 * **A command that writes is matched before a widget's name** (v1.92):
 * `/todo` and `/todos` differ by a letter, and the one that takes text wins.
 * **Every record command says `start-`** (v1.99), so a bare widget name always
 * opens the widget. An unknown `/word` is sent as an ordinary message.
 */

/** What a session timer can record: Turner's Rec offers all eleven (28A). */
export type RecordType =
  | "run"
  | "ride"
  | "walk"
  | "swim"
  | "row"
  | "strength"
  | "yoga"
  | "piano"
  | "reading"
  | "work"
  | "other";

export interface Recordable {
  type: RecordType;
  label: string;
  icon: string;
}

export interface CommandInfo {
  name: string;
  kind: "system" | "record" | "widget" | "skill";
  desc: string;
  /** Takes text after it: accepting it from the palette adds a space. */
  args?: boolean;
}

export const SYSTEM_COMMANDS: CommandInfo[] = [
  { name: "help", kind: "system", desc: "Show all commands" },
  { name: "clear", kind: "system", desc: "Clear the conversation" },
  {
    name: "history",
    kind: "system",
    desc: "Show the earlier messages — scroll up for more; `/clear` hides them again",
  },
  { name: "sync", kind: "system", desc: "Sync data from connected sources now" },
  {
    name: "theme",
    kind: "system",
    args: true,
    desc: "Switch light / dark — `/theme dark`, or bare to toggle",
  },
  {
    name: "note",
    kind: "system",
    args: true,
    desc: "Jot a note into Notes (alias `/n`) — `/note @Travel book the ferry #japan`",
  },
  {
    name: "todo",
    kind: "system",
    args: true,
    desc: "Add a to-do to Today — `/todo ring the dentist #health`, or `@List` to file it",
  },
  {
    name: "issue",
    kind: "system",
    args: true,
    desc: "Report something broken — `/issue the map is blank at z12`. You see what is sent first",
  },
];

export const RECORDABLE: Record<string, Recordable> = {
  "start-strength": { type: "strength", label: "Strength", icon: "🏋️" },
  "start-piano": { type: "piano", label: "Piano", icon: "🎹" },
};

export const RECORD_COMMANDS: CommandInfo[] = [
  { name: "start-strength", kind: "record", desc: "Start a strength session timer" },
  { name: "start-piano", kind: "record", desc: "Start a piano session timer" },
];

export interface Skill {
  slug: string;
  title: string;
  summary?: string | null;
}

export type Command =
  | { kind: "help" }
  | { kind: "clear" }
  | { kind: "history" }
  | { kind: "sync" }
  | { kind: "theme"; arg: string }
  | { kind: "note"; text: string }
  | { kind: "todo"; text: string }
  | { kind: "issue"; text: string }
  | { kind: "record"; recording: Recordable }
  | { kind: "widget"; name: string }
  | { kind: "message"; message: string; skill: string | null };

export function route(
  typed: string,
  widgets: readonly string[],
  skills: readonly Skill[],
): Command | null {
  const text = typed.trim();
  if (!text) return null;
  if (!text.startsWith("/")) return { kind: "message", message: text, skill: null };

  // Split on the first run of whitespace, not a space: `/note` is the command
  // whose text often starts on a new line, and that text keeps its line breaks.
  const afterSlash = text.slice(1).trim();
  const [, word = afterSlash, restRaw = ""] = afterSlash.match(/^(\S+)([\s\S]*)$/) ?? [];
  const cmd = word.toLowerCase();
  const rest = restRaw.trim();

  if (cmd === "help") return { kind: "help" };
  if (cmd === "clear") return { kind: "clear" };
  if (cmd === "history") return { kind: "history" };
  if (cmd === "sync") return { kind: "sync" };
  if (cmd === "theme") return { kind: "theme", arg: rest.toLowerCase() };
  if (cmd === "note" || cmd === "n") return { kind: "note", text: rest };
  if (cmd === "todo") return { kind: "todo", text: rest };
  // Before the widgets and the skills: `/issue` files a report.
  if (cmd === "issue") return { kind: "issue", text: rest };
  const recording = RECORDABLE[cmd];
  if (recording) return { kind: "record", recording };
  if (widgets.includes(cmd)) return { kind: "widget", name: cmd };
  const skill = skills.find((s) => s.slug === cmd);
  if (skill)
    return {
      kind: "message",
      message: rest || `Run the "${skill.title}" skill.`,
      skill: skill.slug,
    };
  return { kind: "message", message: text, skill: null };
}

/** The palette while the command word is typed: a leading `/` and no space yet. */
export function suggest(value: string, all: readonly CommandInfo[]): CommandInfo[] {
  if (!value.startsWith("/") || /\s/.test(value.slice(1))) return [];
  const token = value.slice(1).toLowerCase();
  const found = all.filter((c) => c.name.startsWith(token));
  // An exact, complete command needs no menu.
  return found.length === 1 && found[0]?.name === token ? [] : found;
}

/** The palette's line for a command, once accepted. */
export function accepted(command: CommandInfo): string {
  return `/${command.name}${command.kind === "skill" || command.args ? " " : ""}`;
}

export function helpText(widgets: readonly string[], skills: readonly Skill[]): string {
  const line = (c: CommandInfo) => `- \`/${c.name}\` — ${c.desc}`;
  return [
    "**System**",
    SYSTEM_COMMANDS.map(line).join("\n"),
    "",
    "**Record a session**",
    RECORD_COMMANDS.map(line).join("\n"),
    "",
    "**Open a widget**",
    widgets.map((w) => `\`/${w}\``).join(" · "),
    "",
    "**Run a skill** — add text after to steer it (e.g. `/meal-plan 5 vegetarian dinners`)",
    skills.length
      ? skills.map((s) => `- \`/${s.slug}\` — ${s.summary || s.title}`).join("\n")
      : "_None available._",
  ].join("\n");
}
